import {
  BasketballMessage,
  BasketballMessagesPagination,
  RequestBasketballPostMessage,
  SelectOptionGroup,
} from '@hefe/models';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { isCacheValid } from '../../../utils/cache/caching';

const FEATURE_KEY = 'basketballMessages';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */

export const fetchBasketballUserMessages = createAsyncThunk(
  'basketballMessages/fetchUser',
  async (page: number, thunkAPI) => {
    const response = await API.basketballMessages().getUser(page);
    return response?.data;
  }
);

export const fetchBasketballMessageRecipients = createAsyncThunk(
  'basketballMessages/fetchRecipients',
  async (
    data: {
      team: boolean;
      cacheDuration: number;
    },
    thunkAPI
  ) => {
    if (!isCacheValid('basketballTeam/getPlayersSelect', data, thunkAPI)) {
      const response = await API.basketballMessages().getRecipients(data.team);
      return response?.data;
    }
  }
);

export const createBasketballMessage = createAsyncThunk(
  'basketballMessages/create',
  async (
    data: {
      title: string;
      text: string;
      adminMessage: boolean;
      values: string[];
    },
    thunkAPI
  ) => {
    let toPlayers = false;
    let toCoaches = false;
    let toReferees = false;
    const userRecipients: number[] = [];
    const teamRecipients: number[] = [];

    for (const value of data.values) {
      const type = value[0];
      const id = parseInt(value.substring(1));
      switch (type) {
        case 'g':
          if (id === 1) toCoaches = true;
          if (id === 2) toReferees = true;
          if (id === 3) toPlayers = true;
          break;
        case 't':
          teamRecipients.push(id);
          break;
        case 'u':
          userRecipients.push(id);
          break;
      }
    }

    const request: RequestBasketballPostMessage = {
      title: data.title,
      text: data.text,
      adminMessage: data.adminMessage,
      toCoaches: toCoaches,
      toPlayers: toPlayers,
      toReferees: toReferees,
      team_recipients: teamRecipients,
      user_recipients: userRecipients,
    };
    const response = await API.basketballMessages().save(request);
    return response?.data;
  }
);

export const deleteBasketballMessage = createAsyncThunk<any, BasketballMessage>(
  'basketballMessages/delete',
  async (message: BasketballMessage, thunkAPI) => {
    const response = await API.basketballMessages().delete(message);
    if (response) {
      return thunkAPI.fulfillWithValue(message);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const fetchBasketballAdminMessages = createAsyncThunk(
  'basketballMessages/getAdminMessages',
  async (page: number, thunkAPI) => {
    const response = await API.basketballMessages().getClub(page);
    return response?.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballMessagesState {
  loadingUserMessages: boolean;
  userMessages: BasketballMessagesPagination;
  loadingAdminMessages: boolean;
  adminMessages: BasketballMessagesPagination;
  recipients: SelectOptionGroup[];
}

const initialState: BasketballMessagesState = {
  userMessages: {
    current_page: 0,
    data: [],
    from: 0,
    per_page: 0,
    to: 0,
  },
  loadingUserMessages: false,
  adminMessages: {
    current_page: 0,
    data: [],
    from: 0,
    per_page: 0,
    to: 0,
  },
  loadingAdminMessages: false,
  recipients: [],
};

export const basketballMessagesSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchBasketballUserMessages.pending,
      (state: BasketballMessagesState) => {
        state.loadingUserMessages = true;
      }
    );
    builder.addCase(
      fetchBasketballUserMessages.fulfilled,
      (
        state: BasketballMessagesState,
        action: PayloadAction<BasketballMessagesPagination>
      ) => {
        if (action.payload.current_page === 1) {
          state.userMessages = action.payload;
        } else {
          state.userMessages = {
            ...action.payload,
            data: [...state.userMessages.data, ...action.payload.data],
          };
        }
        state.loadingUserMessages = false;
      }
    );
    builder.addCase(
      fetchBasketballUserMessages.rejected,
      (state: BasketballMessagesState) => {
        state.loadingUserMessages = false;
      }
    );
    builder.addCase(
      fetchBasketballMessageRecipients.fulfilled,
      (
        state: BasketballMessagesState,
        action: PayloadAction<SelectOptionGroup[]>
      ) => {
        if (action.payload) {
          state.recipients = action.payload;
        }
      }
    );
    builder.addCase(
      fetchBasketballAdminMessages.pending,
      (state: BasketballMessagesState) => {
        state.loadingAdminMessages = true;
      }
    );
    builder.addCase(
      fetchBasketballAdminMessages.rejected,
      (state: BasketballMessagesState) => {
        state.loadingAdminMessages = false;
      }
    );
    builder.addCase(
      fetchBasketballAdminMessages.fulfilled,
      (
        state: BasketballMessagesState,
        action: PayloadAction<BasketballMessagesPagination>
      ) => {
        if (action.payload.current_page === 1) {
          state.adminMessages = action.payload;
        } else {
          state.adminMessages = {
            ...action.payload,
            data: [...state.adminMessages.data, ...action.payload.data],
          };
        }
        state.loadingAdminMessages = false;
      }
    );
    builder.addCase(
      createBasketballMessage.fulfilled,
      (
        state: BasketballMessagesState,
        action: PayloadAction<BasketballMessage>
      ) => {
        if (action.payload.adminMessage) {
          state.adminMessages.data.unshift(action.payload);
        }
      }
    );
    builder.addCase(
      deleteBasketballMessage.fulfilled,
      (
        state: BasketballMessagesState,
        action: PayloadAction<BasketballMessage>
      ) => {
        state.adminMessages.data = state.adminMessages.data.filter(
          (message) => message.id !== action.payload.id
        );
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballMessagesReducer = basketballMessagesSlice.reducer;
export const basketballMessagesActions = basketballMessagesSlice.actions;

const getBasketballMessagesState = (rootState: any): BasketballMessagesState =>
  rootState[FEATURE_KEY];

export const basketballUserMessagesSelector = createSelector(
  getBasketballMessagesState,
  (state) => state.userMessages
);

export const basketballUserMessagesLoadingSelector = createSelector(
  getBasketballMessagesState,
  (state) => state.loadingUserMessages
);

export const basketballAdminMessagesSelector = createSelector(
  getBasketballMessagesState,
  (state) => state.adminMessages
);

export const basketballAdminMessagesLoadingSelector = createSelector(
  getBasketballMessagesState,
  (state) => state.loadingAdminMessages
);

export const basketballMessageRecipientsSelector = createSelector(
  getBasketballMessagesState,
  (state) => state.recipients
);
